import { AuthorizationRequest } from '@openid/appauth/built/authorization_request';
import {
	AuthorizationRequestHandler,
	AuthorizationRequestResponse,
} from '@openid/appauth/built/authorization_request_handler';
import {
	AuthorizationError,
	AuthorizationResponse,
} from '@openid/appauth/built/authorization_response';
import { AuthorizationServiceConfiguration } from '@openid/appauth/built/authorization_service_configuration';
import { Crypto, DefaultCrypto } from '@openid/appauth/built/crypto_utils';
import { BasicQueryStringUtils } from '@openid/appauth/built/query_string_utils';
import { PerformAuthorizationRequestResponse } from './TypeDefinitions';
import { Logger } from '../Logger';
import { logConstants } from '../../utils/Constants';
import { authorizationRequestErrorString } from './Errors';
import { SentryLogger } from '../../sentryLogging/SentryLogger';

export class OIDCAuthorizationRequestHandler extends AuthorizationRequestHandler {
	private static instance: OIDCAuthorizationRequestHandler;
	static getInstance(): OIDCAuthorizationRequestHandler {
		if (!OIDCAuthorizationRequestHandler.instance) {
			OIDCAuthorizationRequestHandler.instance = new OIDCAuthorizationRequestHandler();
		}
		return OIDCAuthorizationRequestHandler.instance;
	}

	private constructor(
		utils = new BasicQueryStringUtils(),
		crypto: Crypto = new DefaultCrypto()
	) {
		super(utils, crypto);
	}

	/**
	 * Process perform authorization request. Adds code challenge and code verifier to the request and builds the webview start url
	 *
	 * @param {AuthorizationServiceConfiguration} configuration
	 * @param {AuthorizationRequest} request
	 * @returns {Promise<PerformAuthorizationRequestResponse>}
	 * @memberof OIDCAuthorizationRequestHandler
	 */
	async performAuthorizationRequest(
		configuration: AuthorizationServiceConfiguration,
		request: AuthorizationRequest
	): Promise<PerformAuthorizationRequestResponse> {
		await this.addCodeChallengeToRequest(request);
		const startUrl = this.buildRequestUrl(configuration, request);
		return { startUrl, request };
	}

	async addCodeChallengeToRequest(request: AuthorizationRequest): Promise<void> {
		try {
			await request.setupCodeVerifier();
		} catch (e) {
			SentryLogger.getInstance().logError(e);
			throw new Error(authorizationRequestErrorString.OIDC_SETUP_CODE_VERIFIER_ERROR);
		}
	}

	//empty - won't implement
	protected completeAuthorizationRequest(): Promise<AuthorizationRequestResponse | null> {
		Logger.getInstance().write(
			logConstants.INFO,
			authorizationRequestErrorString[
				'OIDC_COMPLETE_AUTHORIZATION_REQUEST_METHOD_NOT IMPLEMENTED'
			]
		);
		throw new Error(
			authorizationRequestErrorString[
				'OIDC_COMPLETE_AUTHORIZATION_REQUEST_METHOD_NOT IMPLEMENTED'
			]
		);
	}

	/**
	 * Process web view response parser
	 *
	 * @param {AuthorizationServiceConfiguration} webviewResponse
	 * @param {AuthorizationRequest} request
	 * @returns {AuthorizationResponse}
	 * @memberof OIDCAuthorizationRequestHandler
	 */
	parseWebviewResponse(
		webviewResponse: string,
		request: AuthorizationRequest,
		requestId: string
	): AuthorizationResponse {
		const url = new URL(webviewResponse);
		const queryParams = this.utils.parseQueryString(url.search);
		const state: string | undefined = queryParams['state'];
		const code: string | undefined = queryParams['code'];
		const error: string | undefined = queryParams['error'];
		const shouldNotify = state === request.state;
		let authorizationResponse: AuthorizationResponse | null = null;
		let authorizationError: AuthorizationError | null = null;
		if (shouldNotify) {
			if (error) {
				/* eslint-disable @typescript-eslint/naming-convention */
				const errorUri = queryParams['error_uri'];
				const errorDescription = queryParams['error_description'];
				authorizationError = new AuthorizationError({
					error: error,
					error_description: errorDescription,
					error_uri: errorUri,
					state: state,
				});
				/* eslint-enable @typescript-eslint/naming-convention*/
				Logger.getInstance().write(
					logConstants.ERROR,
					requestId +
						': ' +
						authorizationError.error +
						': ' +
						authorizationError.errorDescription +
						': ' +
						authorizationError.errorUri
				);
				throw authorizationError;
			} else {
				Logger.getInstance().write(
					logConstants.INFO,
					requestId + ': Received code successfully'
				);
				authorizationResponse = new AuthorizationResponse({ code: code, state: state });
			}
			return authorizationResponse;
		} else {
			Logger.getInstance().write(
				logConstants.ERROR,
				requestId +
					': ' +
					authorizationRequestErrorString.OIDC_MISMATCHED_STATE_IN_REQUEST_AND_RESPONSE
			);
			throw new Error(
				authorizationRequestErrorString.OIDC_MISMATCHED_STATE_IN_REQUEST_AND_RESPONSE
			);
		}
	}
}
